import type { AxiosRequestConfig } from 'axios';

import type { CategoryDisplayType } from '~/components/CategoryBlocks/CategoryBlock';
import type { DestinationKind } from '~/utils/destinationKind';

import type {
  PaginationRequest,
  ProductFilters,
  ProductSorting,
} from '../stores/CatalogStore';
import { ENDPOINT } from './constants';
import { RequestAPI } from './Requests';
import { getLangHeaders } from './utils';

export interface MultibuyPriceLevel {
  id: number;
  quantity: number;
  unitPrice: number;
  convertedUnitPrice?: Nullable<number>;
}

/** Mix & Match promotion data of an eligible offer. */
export interface MixAndMatchAPI {
  /** Promotional unit price in the base currency, minor units. */
  price: NumberInt;
  /** `price` converted to the company currency, minor units. */
  convertedPrice: Nullable<NumberInt>;
  /** Eligible units across the whole cart that unlock the promotion. */
  threshold: number;
}

export interface RatingDistributionAPI {
  rating1Count: number;
  rating2Count: number;
  rating3Count: number;
  rating4Count: number;
  rating5Count: number;
  totalReviews: number;
}

/** Rating distribution by star 1–5 (sum equals `ratingMarksCount`). */
export type RatingHistogramAPI = Record<'1' | '2' | '3' | '4' | '5', number>;

/**
 * Per-warehouse stock entry returned by catalogue client routes when the
 * request carries no `warehouseCode`. The array is ordered by warehouse
 * priority, so the first entry not matching the current warehouse is the
 * preferred fallback ("parent") stock source.
 */
export interface WarehouseStockAPI {
  warehouseCode: string;
  stock: number | string;
}

/**
 * Badge assigned to a product or offer from the catalog badge reference book.
 *
 * `name` arrives already localized for the requested `x-lang`, with the default
 * language substituted when a translation is missing. Colors are optional —
 * an empty or malformed value means the storefront applies its design-system
 * color instead. Order within the array is the display order.
 */
export interface CatalogBadgeAPI {
  id: number;
  code: string;
  name: string;
  backgroundColor: Nullable<string>;
  textColor: Nullable<string>;
}

/** Localized, backend-sorted warehouse attribute shown in the PDP USP block. */
export interface WarehouseAttributeAPI {
  id: number;
  title: string;
  subtitle: Nullable<string>;
  iconUrl: Nullable<string>;
  sortOrder: number;
}

export interface ProductAPI {
  /** Absent on old backend pods; treat as empty. */
  warehouseAttributes?: WarehouseAttributeAPI[];
  maxQuantityPerOrder?: Nullable<number>;
  /**
   * Catalog badges in display order. Absent on endpoints that do not yet
   * return the field; an empty array means the product has none.
   */
  badges?: Nullable<CatalogBadgeAPI[]>;
  categoryId: number;
  categoryName: string;
  categorySlug: string;
  commodityGroup: { id: number; name: ProductCommodityGroupAPI };
  description: string | null;
  discountPrice: Nullable<NumberInt>;
  id: number;
  slug: string;
  images: Nullable<ImageAPI[]>;
  isFavourite: boolean;
  name: string;
  offerProperties: Nullable<ProductPropertiesAPI[]>;
  offers: ProductOfferAPI[];
  parentCategoryId: number;
  parentCategoryName: string;
  previewImage: ImageAPI;
  previewImageThumb: ImageAPI;
  price: Nullable<NumberInt>;
  sellable: number;
  sellableWarehouses: Record<string, string>;
  /** Priority-ordered per-warehouse stock; present on all-warehouse responses. */
  stocks?: Nullable<WarehouseStockAPI[]>;
  status: Nullable<ProductStatus>;
  code: string;
  isBackorderAvailable: Nullable<boolean>;
  backorderLeadTime: Nullable<number>;
  minBasketDiscountPrice: Nullable<number>;
  minBasketDiscountQuantity: Nullable<number>;
  multibuyPriceLevels: Nullable<MultibuyPriceLevel[]>;
  /** Lowest Mix & Match price among the sellable variants. */
  mixAndMatch?: Nullable<MixAndMatchAPI>;
  /**
   * Present in search/category listings and, with `compactOffers`, on the
   * product page, where it lists the offers not returned in `offers[]`.
   */
  variants?: Variant[];

  // Ответ от Константина: это количество доступное для продажи, относится к бестеллерам, судя по коду
  salesQuantity: number;
  createdAt: string;
  updatedAt: string;
  ratingAverage?: number;
  ratingDistribution?: RatingDistributionAPI;
  ratingMarksCount?: number;
  ratingHistogram?: RatingHistogramAPI | null;
  sortingCategoryPosition: number;
  productType: string;
  metadataTitle: string | null;
  metadataDescription: string | null;
  isBought?: boolean;
  /** `price` converted to the company currency, in minor units. */
  salePrice?: Nullable<NumberInt>;
  /** `discountPrice` converted to the company currency, in minor units. */
  convertedDiscountPrice?: Nullable<NumberInt>;
}

/**
 * A row per offer+property pair in `products[].variants[]`. Search returns
 * only the first five fields; category listings add `images` and `sellable`;
 * the compact product page adds the stock fields.
 */
export interface Variant {
  property: string;
  sku: string;
  value: string;
  sorting: number;
  /**
   * Swatch image of the property VALUE, not of the offer: every row with the
   * same property+value carries the same image. null → render the text label.
   */
  image?: Nullable<ImageAPI>;
  images?: Nullable<ImageAPI[]>;
  sellable?: Nullable<number>;
  isBackorderAvailable?: boolean;
  sellableWarehouses?: Nullable<Record<string, string | number>>;
  stocks?: Nullable<WarehouseStockAPI[]>;
}

export interface ProductOfferAPI {
  /** Absent on old backend pods; treat as empty. */
  warehouseAttributes?: WarehouseAttributeAPI[];
  maxQuantityPerOrder?: Nullable<number>;
  /**
   * Catalog badges in display order. Overrides the parent product's badges
   * when non-empty.
   */
  badges?: Nullable<CatalogBadgeAPI[]>;
  backorderLeadTime: number;
  bundleId: number | null;
  bundleQuantity?: number;
  code: string; // SKU
  description: string | null;
  discountPrice: Nullable<NumberInt>;
  id: number;
  images: Nullable<ImageAPI[]>;
  isBackorderAvailable: boolean;
  minBasketDiscountPrice: number | null;
  minBasketDiscountQuantity: number | null;
  multibuyPriceLevels: MultibuyPriceLevel[] | null;
  mixAndMatch?: Nullable<MixAndMatchAPI>;
  name: string;
  price: Nullable<NumberInt>;
  productId: number;
  properties: ProductPropertiesAPI[];
  sellable: number;
  sellableWarehouses: Record<string, string>;
  /** Priority-ordered per-warehouse stock; present on all-warehouse responses. */
  stocks?: Nullable<WarehouseStockAPI[]>;
  metadataTitle: string | null;
  metadataDescription: string | null;
  productSlug?: string;
  /** Customs duty for cross-border goods, in minor currency units (kopecks). */
  customs_fee?: number | null;
  /** `price` converted to the company currency, in minor units. */
  salePrice?: Nullable<NumberInt>;
  /** `discountPrice` converted to the company currency, in minor units. */
  convertedDiscountPrice?: Nullable<NumberInt>;
}

export type ProductPropertiesKeyAPI =
  | 'RRP'
  | 'abv'
  | 'age_restriction'
  | 'alcohol_units'
  | 'allergen_free'
  | 'backorder_lead_time'
  | 'best_before_text'
  | 'carbohydrates'
  | 'color'
  | 'color_text'
  | 'country_of_origin'
  | 'dairy_free'
  | 'fairtrade'
  | 'fat'
  | 'fat_saturates'
  | 'fibre'
  | 'flavour'
  | 'food_pairing'
  | 'for_halal'
  | 'for_kosher'
  | 'for_vegan'
  | 'for_vegetarians'
  | 'freezing_suitable'
  | 'gluten_free'
  | 'gmo_free'
  | 'ingredients'
  | 'is_backorder_available'
  | 'is_gift'
  | 'is_icecream'
  | 'is_organic_food'
  | 'items_per_outer_retail_pack'
  | 'kcal'
  | 'limited_stock'
  | 'low_fat'
  | 'manufacturer'
  | 'may_contain_nuts'
  | 'no_added_sugar'
  | 'other_nutrition_info'
  | 'other_parameters'
  | 'out_of_circulation'
  | 'own_bakery'
  | 'own_kitchen'
  | 'own_trademark'
  | 'region'
  | 'packing'
  | 'price_lock_tag'
  | 'product_size'
  | 'promo_quantity_discount_price'
  | 'promo_required_quantity'
  | 'proteins'
  | 'safery_allergen'
  | 'salt'
  | 'size'
  | 'storage'
  | 'sugar'
  | 'sugar_free'
  | 'taste'
  | 'tesco_price_match'
  | 'type_of_packaging'
  | 'uom'
  | 'vintage/year'
  | 'with_soya'
  | 'with_sulphites'
  | CityDrinksProductPropertiesKeyAPI
  | MCosmeticProductPropertiesKeyAPI;

export type MCosmeticProductPropertiesKeyAPI = 'tsvet';

export type CityDrinksProductPropertiesKeyAPI =
  | 'express_delivery'
  | 'price'
  | 'cooled'
  | 'packaging'
  | 'can_or_glass_bottle'
  | 'cask_type'
  | 'flavour'
  | 'bio_dynamic'
  | 'limited_edition'
  | 'on_offer'
  | 'food_pairing'
  | 'award_winning'
  | 'grape_or_variety'
  | 'vintage_or_year'
  | 'producer'
  | 'style'
  | 'sweetness'
  | 'unit_of_measurement'
  | 'bottle_size'
  | 'region'
  | 'country'
  | 'type'
  | 'bottling_status'
  | 'best_served'
  | 'volume';

export type ProductPropertiesTypeAPI =
  | 'checkboxGroup'
  | 'color'
  | 'colorList'
  | 'date'
  | 'dateTime'
  | 'file'
  | 'image'
  | 'number'
  | 'radioGroup'
  | 'selector'
  | 'sizeList'
  | 'tagList'
  | 'text'
  | 'textarea'
  | 'time'
  | 'toggle'
  | 'badge';

export type ProductPropertyChoiceAPI = {
  label: string;
  value: string | number;
  sorting: number;
  /** Swatch image of this choice; null → render the text label. */
  image?: Nullable<ImageAPI>;
};

export type ProductPropertiesAPI = {
  additionalPropertyId: Unnecessary<number>;
  choices?: Nullable<ProductPropertyChoiceAPI[]>;
  code: ProductPropertiesKeyAPI;
  isOfferGenerator: boolean;
  isOfferVariants: boolean;
  sorting: number;
  name: string;
  type: ProductPropertiesTypeAPI;
  value?: boolean | string | number;
  values: string | string[];
  /** Whether choices of this variant property carry swatch images (selector type only). */
  containsImage?: boolean;
};

export enum ProductCommodityGroupAPI {
  GROCERY = 'GROCERY',
  ALCOHOL = 'ALCOHOL',
  TOBACCO = 'TOBACCO',
  PHARMA = 'PHARMA',
  CHEMICAL = 'CHEMICAL',
  FROZEN = 'FROZEN',
  OWN_KITCHEN = 'OWN_KITCHEN',
  CLOTHING = 'CLOTHING',
  SHOES = 'SHOES',
  ACCESSORIES = 'ACCESSORIES',
}

type ProductImageAPI = { path?: string };

type ImageAPI = {
  url?: string;
  thumb?: string;
};

export enum LandingPage {
  HOME = 'home',
  SHOP = 'shop',
  HERO_BANNER = 'hero_banner',
}

export const ZONE_LANDING_PAGE_PREFIX = 'zone_';

/** Landing page of one catalog zone, laid out in the CMS like the home page. */
export type ZoneLandingPage = `${typeof ZONE_LANDING_PAGE_PREFIX}${number}`;

export const zoneLandingPage = (zoneId: number): ZoneLandingPage =>
  `${ZONE_LANDING_PAGE_PREFIX}${zoneId}`;

export enum ProductStatus {
  ACTIVE = 'active',
  DRAFT = 'draft',
  CONTENT_READY = 'content_ready',
}

export interface CategoryAPI {
  id: number;
  parentId: number;
  parentSlug: Nullable<string>;
  parentName: Nullable<string>;
  name: string;
  slug: string;
  deepLink: string;
  depth: number;
  tileSize: number;
  previewImage: Nullable<ImageAPI>;
  previewImageThumb: Nullable<ImageAPI>;
  description: Nullable<string>;
  previewText: Nullable<string>;
  landingPage: Nullable<LandingPage | ZoneLandingPage>;
  displayType: Nullable<CategoryDisplayType>;
  fontColor: Nullable<string>;
  backgroundColor: Nullable<string>;
  images: ImageAPI[];
  products: ProductAPI[];
  /** Number of distinct active products in the category; absent until the backend from JS-14414 is deployed */
  productsCount?: number;
  promoEndDate?: Nullable<string>;
  metadataTitle: string | null;
  metadataDescription: string | null;
  showInSupermenu?: boolean;
  icon?: ImageAPI;
}

export interface ICatalogPrices {
  discountPriceMax?: number;
  discountPriceMin?: number;
  priceMax: number;
  priceMin: number;
  salePriceMin?: number;
  salePriceMax?: number;
  convertedDiscountPriceMin?: number;
  convertedDiscountPriceMax?: number;
}

export interface CategoryListAPI {
  category: CategoryAPI;
  children?: CategoryListAPI[];
  filters?: FilterAPI[];
  prices?: ICatalogPrices;
}

export interface CategoryPagedListAPI {
  category: CategoryAPI;
  products?: ProductAPI[];
  filters?: FilterAPI[];
  prices?: ICatalogPrices;
  children?: CategoryListAPI[];
}

export interface SearchResultsResponse {
  data: {
    products: ProductAPI[];
  };
  pagination: PaginationResponse;
}

export interface Banner {
  active: boolean;
  code: string;
  coupon_group: Record<string, unknown>;
  created_at: string;
  date_begin: string;
  date_end: null;
  deep_link: string;
  deleted_at: null;
  description: string;
  display_mode: string;
  hidden: boolean;
  id: number;
  images: unknown;
  name: string;
  offer: number[];
  preview_image: ProductImageAPI | null;
  preview_text: string;
  schedule: Record<string, unknown>;
  slug: string;
  sort_order: number;
  tile_size: number;
  type: string;
  updated_at: string;
}

export interface PaginationResponse {
  current: number;
  size: number;
  total: number;
}

export interface ProductsPagedResponse {
  data: CategoryPagedListAPI;
  pagination: PaginationResponse;
}

export interface OffersRequest {
  warehouseCode?: string;
  sku: string;
  productId?: number;
}

export interface OffersResponse {
  data: ProductOfferAPI[];
}

interface ProductsResponse {
  data: CategoryListAPI;
}

interface ProductResponse {
  data: ProductAPI;
  error?: boolean;
  message?: string;
}

export interface SearchHistoryResponse {
  data: { search_phrase: string | null }[];
}

export interface CategoriesResponse {
  data: CategoryListAPI[];
  pagination?: PaginationResponse;
}

export interface ProductRequest {
  deviceId: string;
  customerId?: string;
  id?: string;
  deepLink?: string;
  isSafeRequest?: boolean;
  /**
   * Return a single offer in full and describe the rest through `variants[]`
   * (one row per variant property of every other offer). Bundles are not
   * affected: their components always arrive in full.
   */
  compactOffers?: boolean;
  /** The offer to return in full with `compactOffers`; defaults to the first sellable one. */
  sku?: string;
}

export interface FullOfferRequest {
  sku: string;
  warehouseCode?: string;
}

export type RecommendProductsSource =
  | 'cart'
  | 'card'
  | 'search'
  | 'purchased_items'
  | 'order_again';

export type CartCalculationItemRequest = {
  sku: string;
  requested_quantity: number;
};

export type CartBundlesCalculationItemRequest = {
  id: number;
  requested_quantity: number;
};

export interface CartCalculationRequest {
  warehouse_code: string;
  zone_id?: string;
  seller: string;
  /** UUID of the pickup point the order is handed over at, when there is one. */
  pickup_point_uuid?: string;
  destination_kind?: DestinationKind;
  pup_type?: 'partner';
  pup_provider?: string;
  pup_external_id?: string;
  pup_id?: string;
  delivery_provider?: string;
  delivery_method?: string;
  delivery_mechanic?: string;
  deliveries: Array<{
    items: (CartCalculationItemRequest | CartBundlesCalculationItemRequest)[];
    slot_delivery_details?: {
      schedule_slot_id: string;
      current_date: number; // seconds here
    };
  }>;
  address: {
    latitude: number;
    longitude: number;
  };
  should_use_loyalty_points: boolean;
  should_use_bonuses: boolean;
  promocode?: string;
  tips_amount: number;
  slot_delivery_details?: {
    schedule_slot_id: string;
    current_date: number;
  };
}

export type CartCalculationItemResponse = {
  actual_quantity: NumberInt;
  image: string;
  is_gift: boolean;
  name: string;
  paid_bonuses: NumberInt;
  parent_category_id: number;
  product_id: number;
  promo_quantity_discount_price: NumberInt;
  promo_required_quantity: number;
  requested_quantity: number;
  sku: string;
  weight: number;
  sellable: number;
  sellableWarehouses: Record<string, string>;
  base_price: NumberInt;
  discount_amount: NumberInt;
  promocode_discount_amount: NumberInt;
  paid_price: NumberInt;
  is_discount_forbidden: boolean;
  discount_price: NumberInt;
  /** `base_price` converted to the company currency (minor units). */
  salePrice?: Nullable<NumberInt>;
  /** `discount_price` converted to the company currency (minor units). */
  convertedDiscountPrice?: Nullable<NumberInt>;
  /**
   * Member discount per unit, in minor units; already inside `discount_amount`
   * and `paid_price`. Absent for non-members and items the club does not cover.
   */
  membership_discount_amount?: Nullable<NumberInt>;
  items?: CartCalculationItemResponse[];
  slug?: string;
  tax_amount: number;
  tax_percent: number;
};

export type CartCalculationBundleResponse = {
  id: number;
  requested_quantity: number;
  price: number;
  images: Nullable<ImageAPI[]>;
  items?: CartCalculationItemResponse[];
  name: string;
};

export type OrderItemBundle = {
  id: string;
  order_id: string;
  created_at: string;
  catalog_bundle_id: string;
  name: string;
  image: string | null;
  quantity: number;
  company_id: string;
  price: number;
  is_picked_partialy: boolean;
  actual_paid_price: number;
};

/** Mindbox loyalty points for a single order of the cart calculation, in points. */
export interface MindboxCartBonusPoints {
  /** Points the customer already holds on the loyalty balance. */
  available: number;
  /** Points the customer earns for this order. */
  earned: number;
  /** Points spent on this order. */
  spent: number;
  /** Max points spendable on this order. */
  availableAmountForCurrentOrder: number;
  /** Points actually applied to this order. */
  spentAmountForCurrentOrder: number;
}

/** The discount step a calculated order reaches. */
export interface CartCalculationMembershipAppliedTier {
  /** Order subtotal the step starts from, in minor units. */
  floorMinor: number;
  percent: number;
  /** Discount of the step on this order, in minor units. */
  amountMinor: number;
}

/** The next discount step above a calculated order. */
export interface CartCalculationMembershipNextTier {
  /** Order subtotal the step starts from, in minor units. */
  floorMinor: number;
  percent: number;
  /** Subtotal still missing to reach the step, in minor units. */
  missingMinor: number;
}

/** The zone's plan calculate offers a customer without benefits here. */
export interface CartCalculationMembershipUpsell {
  planId: string;
  planCode: string;
  planName: string;
  priceMinor: number;
  currency: string;
  trialLength: number;
  trialUnit: string;
  freeDeliveryThresholdMinor: number;
  /** The step this order would reach with the plan. */
  wouldApplyTier: Nullable<CartCalculationMembershipAppliedTier>;
  /** Discount the plan would give on this order, in minor units. */
  wouldSaveMinor: number;
  /** The plan would make this order's paid delivery free. */
  wouldDeliveryBeFree: boolean;
  /** Delivery fee the plan would save, in minor units; `0` unless `wouldDeliveryBeFree`. */
  deliverySavingMinor: number;
}

/**
 * Membership part of a calculated order (`MembershipCartInfo` of the orders
 * service). Absent for other tenants, guests, zones without plans and while
 * memberships are switched off. `upsell` comes whenever benefits do not apply
 * here: no membership, `past_due`, or a member of another zone.
 */
export interface CartCalculationMembership {
  /** Member benefits apply to this order. */
  active: boolean;
  planId: Nullable<string>;
  planCode: Nullable<string>;
  freeDeliveryThresholdMinor: Nullable<number>;
  /** The member's free-delivery threshold was applied instead of the standard one. */
  thresholdApplied: boolean;
  /** Subtotal the discount step is chosen from, in minor units. */
  tierBaseMinor: number;
  appliedTier: Nullable<CartCalculationMembershipAppliedTier>;
  nextTier: Nullable<CartCalculationMembershipNextTier>;
  upsell: Nullable<CartCalculationMembershipUpsell>;
}

export interface CartCalculationOrderResponse {
  warehouse_code: string;
  items: CartCalculationItemResponse[];
  bundles: CartCalculationBundleResponse[];
  is_delivery_free: boolean;
  seller: string;
  address: {
    latitude: number;
    longitude: number;
  };
  received_bonuses: number;
  base_received_bonuses: number;
  bonuses_to_spend: number;
  mindbox_bonus_points?: MindboxCartBonusPoints;
  is_first_order: boolean;
  should_use_loyalty_points: boolean;
  should_use_bonuses: boolean;
  tax_percent: number;
  tax_amount: number;
  delivery_info: {
    base_price: number;
    discount_amount: number;
    paid_bonuses: number;
    paid_price: number;
    promocode_discount_amount: number;
    /** `base_price` converted to the company currency (minor units). */
    converted_base_price?: Nullable<number>;
    /** `paid_price` converted to the company currency (minor units). */
    converted_paid_price?: Nullable<number>;
    /** `promocode_discount_amount` converted to the company currency (minor units). */
    converted_promocode_discount_amount?: Nullable<number>;
    /** Base delivery price in the base currency (no discounts). */
    shipping_cost?: Nullable<number>;
    /** `shipping_cost` converted to the company currency (minor units). */
    converted_shipping_cost?: Nullable<number>;
    /** Final delivery price in the base currency. */
    delivery_price?: Nullable<number>;
    /** `delivery_price` converted to the company currency (minor units). */
    converted_delivery_price?: Nullable<number>;
    delivery_discount_amount?: Nullable<number>;
    converted_delivery_discount_amount?: Nullable<number>;
    delivery_promocode_discount_amount?: Nullable<number>;
    converted_delivery_promocode_discount_amount?: Nullable<number>;
    slot_delivery_details: {
      schedule_slot_id: string;
      current_date: number;
    };
    threshold: number;
  };
  service_fee: number;
  should_add_tax_to_paid_total: boolean;
  base_total: number; // just total price
  discount_total: number;
  promocode_discount: number;
  paid_bonuses: number;
  paid_total: number; // total price + all discounts + delivery
  paid_without_delivery: number; // total price + all discounts
  promocode: string | null;
  promocodeError: string | null;
  promocodeCalculationNote: 'none' | 'partial' | 'all' | null;
  customs_duties_fee?: number;
  registration_fee?: number;
  customs_service_fee?: number;
  /** Membership part of the order; read only by `modules/membership`. */
  membership?: Nullable<CartCalculationMembership>;
  /**
   * Member discount of the order, in minor units. A breakdown only: the
   * backend already put it into the items' `discount_amount` and `paid_price`,
   * so `discount_total` and `paid_total` include it.
   */
  membership_discount_total?: Nullable<number>;
}

export interface CartCalculationOrderError {
  error: boolean;
  minimalOrderValue: number;
  orderPriceWithoutDelivery: number;
}

/** Part of the cart coming from a single partner warehouse. */
export interface CartCalculationDelivery {
  /** Null for the company's main account. */
  supplier_partner_code: Nullable<string>;
  items: CartCalculationItemResponse[];
}

/** Group of deliveries charged with a single delivery price. */
export interface CartCalculationDeliveryGroup {
  delivery_group_price: number;
  /** `delivery_group_price` converted to the company currency (minor units). */
  converted_delivery_group_price: Nullable<number>;
  deliveries: CartCalculationDelivery[];
}

/** Per-company pickup-slot policy for click-and-collect carts (see CartStore.pickupSlotsPolicy). */
export interface PickupSlotsPolicy {
  /** Pickup slots may be selected; `false` ⇒ ASAP pickup only. */
  enabled: boolean;
  /** A slot must be selected before the order can be placed. */
  required: boolean;
}

export interface CartCalculationResponse {
  /** Present for CLICK_AND_COLLECT carts only. */
  pickup_slots?: PickupSlotsPolicy;
  base_total: number;
  discount_total: number;
  promocode_discount: number;
  paid_bonuses: number;
  paid_external_bonuses: number;
  paid_loyalty_points: number;
  paid_total: number;
  /** `base_total` converted to the company currency (minor units). */
  convertedTotal?: Nullable<number>;
  /** `discount_total` converted to the company currency (minor units). */
  convertedDiscountTotal?: Nullable<number>;
  /** `paid_total` converted to the company currency (minor units) — the actual amount the customer pays. */
  convertedPaidTotal?: Nullable<number>;
  /** `promocode_discount` converted to the company currency (minor units). */
  convertedPromocodeDiscount?: Nullable<number>;
  customsDutiesFeeTotal?: Nullable<number>;
  registrationFee?: Nullable<number>;
  /** Customs service fee (minor units), not the duty. `customsDutiesFee` is the legacy alias. */
  customsDutiesServiceFee?: Nullable<number>;
  customsDutiesFee?: Nullable<number>;
  /**
   * Delivery charged once for the whole cart (minor units). When present, the
   * per-order `delivery_info` prices come back zeroed.
   */
  deliveryGroupPrice?: Nullable<number>;
  /** `deliveryGroupPrice` converted to the company currency (minor units). */
  convertedDeliveryGroupPrice?: Nullable<number>;
  delivery_groups?: Nullable<CartCalculationDeliveryGroup[]>;
  orders: CartCalculationOrderResponse[];
  tax_amount: number;
  tax_percent: number;
  minimal_order_value_error: CartCalculationOrderError[];
  availableAmountForCurrentOrder?: number;
  /** Mindbox loyalty points the customer earns for the whole cart, in points. */
  earned_bonus_points?: number;
}

export type FilterChoiceAPI = {
  label: string;
  value: string;
  isFinal: boolean;
  count?: number;
};

export interface FilterAPI {
  code: ProductPropertiesKeyAPI;
  name: string;
  type: ProductPropertiesTypeAPI;
  values: FilterChoiceAPI[];
  showExpanded: boolean;
  classes?: {
    root?: string;
    inner?: string;
    bodyContainer?: string;
  };
}

export enum RecommendationSource {
  CARD_ASSOCIATIONS = 'card_associations',
  CARD_SIMILAR = 'card_similar',
  CART = 'cart',
  FREQUENTLY_BOUGHT = 'bought_together',
}

export interface Paginated<T> {
  data: T[];
  pagination: PaginationResponse;
}

export interface RecommendationRequestPayload {
  skus: string[];
  warehouseCode?: string;
  source: RecommendationSource;
}

export type SortingFieldName =
  | 'salesQuantity'
  | 'name'
  | 'price'
  | 'createdAt'
  | 'reviewsCount';
export type SortingOrder = 'ASC' | 'DESC';
export type SortingField = `${SortingFieldName}.${SortingOrder}`;

interface CatalogRequestsInterface {
  calculateCart(data: CartCalculationRequest): Promise<CartCalculationResponse>;

  getCategories(
    data: {
      warehouseCode?: string;
      landingPage?: (LandingPage | ZoneLandingPage)[];
      depth?: number;
      parentId?: string | number;
      includes?: { products?: number; empty?: boolean };
      page?: PaginationRequest['page'];
    },
    lang?: string,
  ): Promise<CategoriesResponse>;

  getOffers(data: OffersRequest): Promise<OffersResponse>;

  /**
   * One offer in the same shape the product page embeds it in `offers[]`.
   * The endpoint takes exactly one `sku` and rejects `productId`.
   */
  getFullOffer(data: FullOfferRequest, lang?: string): Promise<OffersResponse>;

  getProducts(
    categoryId: string,
    data: {
      categoryId?: string;
      deepLink?: string;
      isSafeRequest?: boolean;
    } & Partial<ProductFilters> &
      ProductSorting,
  ): Promise<ProductsResponse>;

  getProductsPaged(
    categoryId: string,
    data: {
      categoryId?: string;
      deepLink?: string;
      isSafeRequest?: boolean;
    } & Partial<ProductFilters> &
      ProductSorting &
      PaginationRequest,
    lang?: string,
    config?: AxiosRequestConfig,
  ): Promise<ProductsPagedResponse>;

  /**
   * Gets category tree (parent category and their sub categories) products count (filters allowed)
   *
   * Catalogue service V2 API
   *
   * @param categoryId
   * @param data
   */
  getProductsCount(
    categoryId: string,
    data: {
      categoryId?: string;
      deepLink?: string;
      isSafeRequest?: boolean;
    } & Partial<ProductFilters>,
  ): Promise<ProductsResponse>;

  getProduct(
    productId: string,
    data: ProductRequest,
    lang?: string,
  ): Promise<ProductResponse>;

  getSearch(data: {
    query: string;
    warehouseCode?: string;
    deviceId: string;
    page: PaginationRequest['page'];
  }): Promise<SearchResultsResponse>;

  getSearchHistory(data: { device_id: string }): Promise<SearchHistoryResponse>;

  deleteSearchHistory(data: { device_id: string }): Promise<any>;

  getBestsellers(data: {
    warehouse_code: string;
    isSafeRequest?: boolean;
  }): Promise<{ data: ProductAPI[] }>;

  getRecommendProducts(
    warehouse_code: string,
    data: {
      sku: string[];
      source: RecommendProductsSource;
      isSafeRequest?: boolean;
    },
  ): Promise<{ data: ProductAPI[] }>;

  getFilters(): Promise<{ data: FilterAPI[] }>;

  getRecommendations(
    params: RecommendationRequestPayload,
  ): Promise<Paginated<ProductAPI>>;

  getOrderAgainRecommendations(
    data: {
      warehouseCode?: string;
      source: 'order_again';
    },
    config?: { signal?: AbortSignal },
  ): Promise<{ data: ProductAPI[] }>;

  getTopPicksRecommendations(
    data: {
      warehouseCode?: string;
      source: 'top_picks';
    },
    config?: { signal?: AbortSignal },
  ): Promise<{ data: ProductAPI[] }>;
}

export const CatalogRequests: CatalogRequestsInterface = {
  calculateCart: (data) =>
    RequestAPI.post(ENDPOINT.orders.calculate, data, getLangHeaders()),

  getCategories: (data, lang) =>
    RequestAPI.get(ENDPOINT.catalog.category.list, data, getLangHeaders(lang)),

  getOffers: (data) =>
    RequestAPI.get(ENDPOINT.catalog.product.offers, data, getLangHeaders()),

  getFullOffer: (data, lang) =>
    RequestAPI.get(
      ENDPOINT.catalog.product.offers,
      { ...data, full: true },
      getLangHeaders(lang),
    ),

  getProducts: (categoryId, data) =>
    RequestAPI.get(
      ENDPOINT.catalog.product.listByCategory + categoryId,
      data,
      getLangHeaders(),
    ),

  getProductsPaged: (categoryId, data, serverlang, config) =>
    RequestAPI.get(
      ENDPOINT.catalog.product.listByCategoryPages + categoryId + '/products',
      data,
      { ...getLangHeaders(serverlang), ...config },
    ),

  getProductsCount: (categoryId, data) =>
    RequestAPI.get(
      ENDPOINT.catalog.product.listByCategory + categoryId + '/count',
      data,
      getLangHeaders(),
    ),

  getProduct: (productId, data, lang) =>
    RequestAPI.get(
      ENDPOINT.catalog.product.request + productId,
      data,
      getLangHeaders(lang),
    ),

  getSearch: (data) =>
    RequestAPI.get(ENDPOINT.catalog.search.request, data, getLangHeaders()),

  getSearchHistory: (data) =>
    RequestAPI.get(
      ENDPOINT.catalog.search.history.list,
      data,
      getLangHeaders(),
    ),

  deleteSearchHistory: (data) =>
    RequestAPI.post(
      ENDPOINT.catalog.search.history.delete,
      data,
      getLangHeaders(),
    ),

  getBestsellers: (data) =>
    RequestAPI.get(
      ENDPOINT.catalog.product.bestsellers,
      data,
      getLangHeaders(),
    ),

  getRecommendProducts: (warehouse_code, data) =>
    RequestAPI.post(
      ENDPOINT.catalog.product.recommendations + warehouse_code,
      data,
      getLangHeaders(),
    ),

  getFilters: () =>
    RequestAPI.get(ENDPOINT.catalog.filters.list, {}, getLangHeaders()),

  getRecommendations: (params) =>
    RequestAPI.get(
      ENDPOINT.catalog.recommendations.list,
      params,
      getLangHeaders(),
    ),

  getOrderAgainRecommendations: (params, config) =>
    RequestAPI.get(ENDPOINT.catalog.recommendations.list, params, {
      ...getLangHeaders(),
      ...config,
    }),

  getTopPicksRecommendations: (params, config) =>
    RequestAPI.get(ENDPOINT.catalog.recommendations.list, params, {
      ...getLangHeaders(),
      ...config,
    }),
};
